import { AppShell } from '../components/AppShell'
import { apiErrorMessage, useInitKyc, useKyc } from '../lib/api'
import { SettingsSidebar } from './SettingsPage'

const META: Record<string, { label: string; cls: string; description: string }> = {
  not_started: { label: 'Not started', cls: 'chip',
    description: 'KYC is required before your first funded payout — about 5 minutes start to finish.' },
  pending:     { label: 'Pending',     cls: 'chip chip--pending',
    description: 'We have your documents and the provider is reviewing them. Most decisions arrive within an hour.' },
  approved:    { label: 'Approved',    cls: 'chip chip--won',
    description: 'You are verified. No further action needed.' },
  rejected:    { label: 'Rejected',    cls: 'chip chip--lost',
    description: 'KYC was rejected. Contact support to find out why and re-submit if applicable.' },
}

const STEPS = [
  { num: 1, title: 'Personal info',  desc: 'Legal name, date of birth, nationality.' },
  { num: 2, title: 'ID document',    desc: 'Passport, ID card, or driver license.' },
  { num: 3, title: 'Selfie check',   desc: 'Liveness + face match against your ID.' },
  { num: 4, title: 'Address proof',  desc: 'Utility bill or bank statement within 90 days.' },
]

export function KycPage() {
  const kyc = useKyc()
  const init = useInitKyc()

  const status = (kyc.data?.kycStatus ?? 'not_started') as string
  const meta = META[status] ?? META.not_started

  return (
    <AppShell page="kyc" breadcrumb={<span className="topbar__breadcrumb-current">Settings · KYC</span>}>
      <header className="page-header">
        <div className="page-header__main">
          <p className="page-header__eyebrow"><span>Account · Compliance</span></p>
          <h1 className="page-header__title">Identity verification</h1>
          <p className="page-header__subtitle">{meta.description}</p>
        </div>
        <div className="page-header__actions">
          <span className={meta.cls}>
            <span className="chip__dot" aria-hidden />
            {meta.label}
          </span>
        </div>
      </header>

      <div className="settings-layout">
        <SettingsSidebar active="kyc" />

        <div className="settings-content">
          <section className="settings-card">
            <header className="settings-card__header">
              <h2 className="settings-card__title">Four-step flow</h2>
              <p className="settings-card__description">Our KYC partner handles ID and selfie checks securely.</p>
            </header>
            <div className="settings-card__body">
              <ol style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', listStyle: 'none', padding: 0 }}>
                {STEPS.map((s) => (
                  <li key={s.num} style={{ display: 'flex', gap: 'var(--space-3)', padding: 'var(--space-3)',
                    background: 'var(--bg-subtle)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      width: 32, height: 32, borderRadius: 999, background: 'var(--color-primary)',
                      color: 'var(--text-on-primary)', fontSize: 'var(--text-sm)', fontWeight: 600, flexShrink: 0 }}>
                      {s.num}
                    </span>
                    <div>
                      <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{s.title}</div>
                      <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>{s.desc}</div>
                    </div>
                  </li>
                ))}
              </ol>

              {init.isError && (
                <div className="auth-card__error" role="alert" style={{ display: 'flex', marginTop: 'var(--space-4)' }}>
                  {apiErrorMessage(init.error)}
                </div>
              )}

              <div className="save-bar" style={{ marginTop: 'var(--space-4)' }}>
                <div className="save-bar__status" />
                <div className="save-bar__actions">
                  <button type="button" className="btn btn--primary"
                    disabled={init.isPending || status === 'approved'}
                    onClick={() => init.mutate()}>
                    {status === 'approved' ? 'Already verified'
                      : status === 'pending'  ? 'Continue verification'
                      : init.isPending        ? 'Starting…'
                      : 'Start KYC'}
                  </button>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </AppShell>
  )
}
